import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { State } from '../../types/state'
import { useSelector } from 'react-redux'
import { Plant } from '../../types/plant'
import AppNavBar from '../Header/AppNavBar'
import ButtonLink from '../Buttons/ButtonLink'

const PlantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const requests = useRequests()
  const { isSuperadmin } = useSelector((state: State) => state.user)

  // Dodanie referencji do obsługi podwójnych żądańs
  const isTrackingRef = useRef(false)

  const [plant, setPlant] = useState<Plant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openedImage, setOpenedImage] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)

  // Zmodyfikowany useEffect do śledzenia odwiedzin strony rośliny
  useEffect(() => {
    const trackVisit = async () => {
      if (id && !isTrackingRef.current) {
        // Ustaw flagę, że trwa śledzenie
        isTrackingRef.current = true

        try {
          await requests.trackPlantVisit(Number(id))
        } catch (error) {
          console.error('Błąd podczas śledzenia odwiedzin rośliny:', error)
        } finally {
          // Odznacz flagę po pewnym czasie, aby zapobiec podwójnym wywołaniom
          setTimeout(() => {
            isTrackingRef.current = false
          }, 1000)
        }
      }
    }

    trackVisit()
  }, [id, requests])

  // Pobieranie danych rośliny
  useEffect(() => {
    const fetchPlantDetails = async () => {
      setLoading(true)
      try {
        const response = await requests.getPlant(Number(id))
        const plantData = response.data

        // Po załadowaniu rośliny, pobieramy presigned URL dla zdjęć
        try {
          const presignedUrlsResponse = await requests.getPlantPresignedUrls(plantData.id)
          // Dodajemy presigned URL do danych rośliny
          plantData.imagePresignedUrl = presignedUrlsResponse.data.imagePresignedUrl
          plantData.imagesPresignedUrls = presignedUrlsResponse.data.imagesPresignedUrls
        } catch (error) {
          console.error('Błąd podczas pobierania presigned URL dla zdjęć rośliny:', error)
        }

        setPlant(plantData)
        setError(null)
      } catch (err) {
        console.error('Błąd podczas pobierania danych rośliny:', err)
        setError('Nie udało się pobrać danych rośliny.')
        setPlant(null)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchPlantDetails()
    }
  }, [id, requests])

  // Funkcja do formatowania tekstu z nową linią jako HTML
  const formatText = (text: string) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
  }

  // Dodajemy funkcję otwierającą zdjęcie w powiększeniu
  const openImageModal = (imageUrl: string) => {
    setOpenedImage(imageUrl)
  }

  // Dodajemy funkcję zamykającą modal
  const closeImageModal = () => {
    setOpenedImage(null)
  }

  // Dodajemy funkcję do obsługi usuwania rośliny
  const handleDeleteClick = async () => {
    try {
      await requests.deletePlant(Number(id))
      navigate('/plants')
    } catch (err) {
      console.error('Błąd podczas usuwania rośliny:', err)
      setError('Nie udało się usunąć rośliny.')
    } finally {
      setDeleteConfirmOpen(false)
    }
  }

  if (loading) {
    return (
      <section className='loading-section py-80'>
        <div className='container'>
          <div className='loading-container text-center py-5'>
            <div className='spinner-border spinner-border-lg text-primary mb-3' style={{ width: '3rem', height: '3rem' }} role='status'>
              <span className='sr-only'>Ładowanie...</span>
            </div>
            <h4 className='mt-3'>Trwa ładowanie szczegółów rośliny</h4>
            <p className='text-muted'>Proszę czekać...</p>
          </div>
        </div>
      </section>
    )
  }

  if (error || (plant == null)) {
    return (
      <div className='error-container text-center py-5'>
        <h3>Wystąpił błąd</h3>
        <p>{error || 'Nie znaleziono rośliny o podanym identyfikatorze.'}</p>
        <button className='btn btn__primary mt-3' onClick={() => navigate('/plants')}>
          Powrót do wyszukiwania
        </button>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        centerText={plant.name}
        rightIcons={[]}
      />
      <section className='plant-details pt-60 pb-60'>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-5 col-md-12 mb-40 mb-lg-0'>
              <div className='plant-details__gallery'>
                <div className='plant-details__main-img mb-30'>
                  {(plant.imagePresignedUrl || plant.image) && (
                    <img
                      src={plant.imagePresignedUrl || plant.image}
                      alt={plant.name}
                      className='img-fluid cursor-pointer'
                      onClick={e => openImageModal(e.currentTarget.src)}
                      style={{ cursor: 'pointer' }}
                      onError={(e) => {
                        console.error('Błąd ładowania głównego zdjęcia:', e)
                        if (plant.image && e.currentTarget.src !== plant.image) {
                          e.currentTarget.src = plant.image
                        } else {
                          e.currentTarget.src = '/default-plant-image.jpg'
                        }
                      }}
                    />
                  )}
                </div>

                {(((plant.imagesPresignedUrls != null) && plant.imagesPresignedUrls.length > 0) ||
                  ((plant.images != null) && plant.images.length > 0)) && (
                    <div className='row'>
                      {(plant.images || []).map((img, index) => (
                        <div key={index} className='col-4'>
                          <div className='plant-details__img'>
                            <img
                              src={plant.imagesPresignedUrls?.[index] || img}
                              alt={`${plant.name} - zdjęcie ${index + 1}`}
                              className='img-fluid'
                              onClick={e => openImageModal(e.currentTarget.src)}
                              style={{ cursor: 'pointer' }}
                              onError={(e) => {
                                console.error(`Błąd ładowania dodatkowego zdjęcia ${index}:`, e)
                                if (img && e.currentTarget.src !== img) {
                                  e.currentTarget.src = img
                                } else {
                                  e.currentTarget.src = '/default-plant-image.jpg'
                                }
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                )}
              </div>
            </div>

            <div className='col-lg-7 col-md-12'>
              <div className='plant-details__info'>
                <h2 className='plant-details__title'>{plant.name}</h2>
                <p className='plant-details__latin-name font-italic mb-4'>{plant.latinName}</p>

                <div className='plant-details__tags mb-4'>
                  {plant.category && (
                    <div className='plant-details__tag-group mb-2'>
                      <span className='plant-details__tag-title fw-bold'>Grupa użytkowa: </span>
                      <span className='plant-details__tag bg-success text-white'>{plant.category}</span>
                    </div>
                  )}
                  {plant.sunExposure && plant.sunExposure.length > 0 && (
                    <div className='plant-details__tag-group'>
                      <span className='plant-details__tag-title fw-bold'>Nasłonecznienie: </span>
                      {plant.sunExposure.map((expo, index) => (
                        <span key={index} className='plant-details__tag bg-warning text-dark me-2'>{expo} </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className='plant-details__content-block p-4 bg-light rounded border mb-4'>
                  <h3 className='border-bottom pb-2 mb-3'>Opis</h3>
                  <div className='plant-description'>
                    {formatText(plant.description)}
                  </div>
                </div>

                <div className='plant-details__specs p-4 bg-light rounded border'>
                  <h4 className='plant-details__section-title border-bottom pb-2 mb-4'>Specyfikacja</h4>
                  <div className='row'>
                    <div className='col-sm-6'>
                      <ul className='plant-details__specs-list'>
                        {!!plant.height && (
                          <li><span>Docelowa wysokość:</span> {plant.height} m</li>
                        )}
                        {!!plant.spread && (
                          <li><span>Docelowa szerokość:</span> {plant.spread} m</li>
                        )}
                        {plant.hardiness && plant.hardiness.length > 0 && (
                          <li>
                            <span>Strefa mrozoodporności:</span> {plant.hardiness.join(', ')}
                          </li>
                        )}
                        {plant.growthRate && (
                          <li><span>Siła wzrostu:</span> {plant.growthRate}</li>
                        )}
                        {plant.leafType && (
                          <li><span>Zimozieloność liści(igieł):</span> {plant.leafType}</li>
                        )}
                        {(plant.fruits != null) && plant.fruits.length > 0 && (
                          <li>
                            <span>Owoce:</span> {plant.fruits.join(', ')}
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className='col-sm-6'>
                      <ul className='plant-details__specs-list'>
                        {plant.leafColor && plant.leafColor.length > 0 && (
                          <li>
                            <span>Barwa liści:</span> {plant.leafColor.join(', ')}
                          </li>
                        )}
                        {plant.flowerColor && plant.flowerColor.length > 0 && (
                          <li>
                            <span>Barwa kwiatów:</span> {plant.flowerColor.join(', ')}
                          </li>
                        )}
                        {plant.soil && plant.soil.length > 0 && (
                          <li>
                            <span>Rodzaj gleby:</span> {plant.soil.join(', ')}
                          </li>
                        )}
                        {(plant.soilMoisture != null) && plant.soilMoisture.length > 0 && (
                          <li>
                            <span>Wilgotność gleby:</span> {plant.soilMoisture.join(', ')}
                          </li>
                        )}
                        {(plant.soilPh != null) && plant.soilPh.length > 0 && (
                          <li>
                            <span>pH podłoża:</span> {plant.soilPh.join(', ')}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className='plant-details__additional-features mt-4 p-4 bg-light rounded border'>
                  <h4 className='plant-details__section-title border-bottom pb-2 mb-4'>Dodatkowe cechy</h4>
                  <div className='row'>
                    <div className='col-sm-6'>
                      {(plant.form != null) && plant.form.length > 0 && (
                        <div className='mb-3'>
                          <h5 className='plant-details__feature-title'>Forma rośliny:</h5>
                          <p>{plant.form.join(', ')}</p>
                        </div>
                      )}

                      {(plant.habit != null) && plant.habit.length > 0 && (
                        <div className='mb-3'>
                          <h5 className='plant-details__feature-title'>Pokrój:</h5>
                          <p>{plant.habit.join(', ')}</p>
                        </div>
                      )}

                      {(plant.floweringSeason != null) && plant.floweringSeason.length > 0 && (
                        <div className='mb-3'>
                          <h5 className='plant-details__feature-title'>Pora kwitnienia:</h5>
                          <p>{plant.floweringSeason.join(', ')}</p>
                        </div>
                      )}

                      {(plant.fruitingSeason != null) && plant.fruitingSeason.length > 0 && (
                        <div className='mb-3'>
                          <h5 className='plant-details__feature-title'>Pora owocowania:</h5>
                          <p>{plant.fruitingSeason.join(', ')}</p>
                        </div>
                      )}
                    </div>

                    <div className='col-sm-6'>
                      {(plant.features != null) && plant.features.length > 0 && (
                        <div className='plant-details__properties mt-40'>
                          <h4 className='plant-details__section-title mb-20'>Walory</h4>
                          <ul className='plant-details__properties-list'>
                            {plant.features.map((feature, index) => (
                              <li key={index}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {(plant.gardenTypes != null) && plant.gardenTypes.length > 0 && (
                        <div className='mb-3'>
                          <h5 className='plant-details__feature-title'>Rodzaje ogrodów:</h5>
                          <ul className='plant-details__feature-list'>
                            {plant.gardenTypes.map((garden, index) => (
                              <li key={index}>{garden}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {(plant.usage != null) && plant.usage.length > 0 && (
                    <div className='mt-3'>
                      <h5 className='plant-details__feature-title'>Zastosowanie:</h5>
                      <p>{plant.usage.join(', ')}</p>
                    </div>
                  )}
                </div>

                {(plant.nurseries != null) && plant.nurseries.length > 0 && (
                  <>
                    <div className='plant-details__nurseries mt-4 p-4 bg-light rounded border'>
                      <h4 className='plant-details__section-title border-bottom pb-2 mb-4'>Dostępność w szkółkach</h4>
                      <div className='table-responsive'>
                        <table className='table table-striped table-hover'>
                          <thead>
                            <tr>
                              <th>Nazwa szkółki</th>
                              <th>Lokalizacja</th>
                              <th>Dostępność</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {plant.nurseries.map(nursery => (
                              <tr key={nursery.id}>
                                <td>
                                  {nursery.website
                                    ? (
                                      <a href={`https://${nursery.website}`} target='_blank' rel='noopener noreferrer'>
                                        {nursery.name}
                                      </a>
                                      )
                                    : (
                                        nursery.name
                                      )}
                                </td>
                                <td>{nursery.location}</td>
                                <td>{nursery.availability === 'available' || nursery.availability === 'dostępny' ? 'Dostępna' : 'Niedostępna'}</td>
                                <td>
                                  <ButtonLink
                                    to={`/nurseries/${nursery.id}`}
                                    className='tw-bg-white tw-shadow-lg'
                                    style={{
                                      color: '#0e2b3d'
                                    }}
                                  >
                                    <i className='icon-arrow-right mr-1' />
                                  </ButtonLink>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className='plant-actions mt-4 tw-flex tw-flex-wrap md:tw-flex-nowrap md:tw-items-center md:tw-justify-between tw-gap-3 md:tw-gap-2'>
                {isSuperadmin && <button
                  className={`
                      tw-w-full md:tw-w-fit tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                    `}
                  onClick={() => navigate(`/plants/edit/${plant.id}`)}
                                 >
                  <i className='fas fa-edit me-2' /> Edytuj roślinę
                </button>}
                {isSuperadmin && <button
                  className={`
                      tw-w-full md:tw-w-fit tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                    `}
                  onClick={() => setDeleteConfirmOpen(true)}
                                 >
                  <i className='fas fa-trash-alt me-2' /> Usuń roślinę
                </button>}
                <button
                  className={`
                      tw-w-full md:tw-w-fit tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-dark-teal hover:tw-bg-opacity-90 focus:tw-bg-dark-teal
                    `}
                  onClick={() => window.print()}
                >
                  <i className='icon-printer me-2' /> Drukuj informacje
                </button>
                <button
                  className={`
                      tw-w-full md:tw-w-fit tw-text-center tw-font-semibold tw-rounded-md tw-shadow-lg tw-transition-colors tw-text-teal
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-white hover:tw-bg-opacity-90 focus:tw-bg-white
                    `}
                  onClick={() => navigate(-1)}
                >
                  <i className='icon-arrow-left me-2' /> Powrót do wyszukiwania
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Modal do powiększania zdjęć */}
      {openedImage && (
        <div
          className='image-modal'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={closeImageModal}
        >
          <div
            className='image-modal__content'
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              animation: 'fadeIn 0.3s'
            }}
          >
            <img
              src={openedImage}
              alt='Powiększone zdjęcie'
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 100px)',
                display: 'block',
                boxShadow: '0 5px 20px rgba(0,0,0,0.5)',
                borderRadius: '4px'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className='image-modal__close'
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '30px',
                cursor: 'pointer'
              }}
              onClick={closeImageModal}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usuwania */}
      {deleteConfirmOpen && (
        <div
          className='delete-modal'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <div
            className='delete-modal__content'
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center'
            }}
          >
            <h4 className='mb-3'>Potwierdź usunięcie</h4>
            <p className='mb-4'>
              Czy na pewno chcesz usunąć roślinę "{plant?.name}"? Tej operacji nie można cofnąć.
            </p>
            <div className='d-flex justify-content-center gap-3'>
              <button
                className='btn btn-secondary'
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Anuluj
              </button>
              <button
                className='btn btn-danger'
                onClick={handleDeleteClick}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PlantDetails
